/**
 * Chọn loại nội dung cần thêm mới (`/ban-tin/chon-loai`) — trang 'bien-tap' của
 * bản mẫu CMS: nút "Quay lại", tiêu đề giữa, lưới sáu ô vuông 220px bo 16px.
 *
 * Mỗi ô dẫn tới luồng THẬT đang có của hệ thống:
 *   - Tải file từ thiết bị  → MH-12 (đoạn âm thanh tải lên);
 *   - Chuyển văn bản thành giọng đọc → MH-13;
 *   - Cắt ghép file        → MH-12 (nhiều đoạn, đổi thứ tự, ghép khi gửi duyệt);
 *   - Bản tin trực tiếp     → MH-22 phát trực tiếp;
 *   - Nguồn từ Cục TTCS     → Kho thông tin nguồn (trang đó còn thiếu API).
 * Ô "Nhập văn bản trực tiếp" (bản tin ký tự thuần, không âm thanh) chưa có
 * luồng: vẫn hiện đúng bố cục, gắn nhãn "Chưa có API", không điều hướng.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { vi } from '../ngon-ngu/vi';
import { viBienTapNoiDung as t } from '../ngon-ngu/vi-bien-tap';
import { NhanChuaCoApi } from '../thanh-phan/MauGiaoDien';
import './bien-tap.css';

export type TenBieuTuongNoiDung =
  | 'tai-len'
  | 'giong-doc'
  | 'but'
  | 'cat'
  | 'song'
  | 'tep'
  | 'loa'
  | 'video'
  | 'anh'
  | 'quay-lai'
  | 'sua'
  | 'tich';

const NET: Record<TenBieuTuongNoiDung, ReactNode> = {
  'tai-len': <path d="M12 16V4m0 0l-5 5m5-5l5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />,
  'giong-doc': (
    <>
      <path d="M4 6h10M4 10h7M4 14h5" />
      <path d="M15 14a3 3 0 0 0 3-3V8a3 3 0 0 0-6 0v3a3 3 0 0 0 3 3zm-5-2a5 5 0 0 0 10 0m-5 5v3" />
    </>
  ),
  but: <path d="M4 20h4L19 9l-4-4L4 16v4zM13 7l4 4" />,
  cat: (
    <>
      <circle cx="6" cy="7" r="3" />
      <circle cx="6" cy="17" r="3" />
      <path d="M8.5 8.5L20 19M8.5 15.5L20 5" />
    </>
  ),
  song: <path d="M3 12h3l3-7 4 14 3-7h5" />,
  tep: <path d="M6 3h8l4 4v14H6zM14 3v4h4M9 12h6M9 16h6" />,
  loa: <path d="M4 9v6h4l5 4V5L8 9H4zM16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" />,
  video: <path d="M3 7h12v10H3zM15 10l6-3v10l-6-3" />,
  anh: (
    <>
      <path d="M3 5h18v14H3z" />
      <path d="M3 16l5-5 4 4 3-3 6 6" />
      <circle cx="16" cy="9" r="1.5" />
    </>
  ),
  'quay-lai': <path d="M10 6l-6 6 6 6M4 12h16" />,
  sua: <path d="M4 20h4L19 9l-4-4L4 16v4z" />,
  tich: <path d="M5 12l5 5L20 7" />,
};

/** Biểu tượng nét dùng riêng cho nhóm biên tập nội dung; luôn đi kèm nhãn chữ. */
export function BieuTuongNoiDung({
  ten,
  co = 22,
  net = 1.8,
}: {
  ten: TenBieuTuongNoiDung;
  co?: number;
  net?: number;
}): ReactNode {
  return (
    <svg
      width={co}
      height={co}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={net}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {NET[ten]}
    </svg>
  );
}

interface ONoiDung {
  khoa: string;
  nhan: string;
  phu?: string;
  bieuTuong: TenBieuTuongNoiDung;
  /** Không có thì ô chưa có chức năng thật: khoá, không điều hướng. */
  toi?: string;
  /** Có điều hướng nhưng trang đích còn thiếu API. */
  thieuApi?: boolean;
}

const DANH_SACH_O: readonly ONoiDung[] = [
  {
    khoa: 'tai-file',
    nhan: t.chonLoai.taiFile,
    phu: t.chonLoai.taiFilePhu,
    bieuTuong: 'tai-len',
    toi: '/ban-tin/moi?loai=tai-file',
  },
  { khoa: 'tts', nhan: t.chonLoai.tts, bieuTuong: 'giong-doc', toi: '/ban-tin/tao-tu-van-ban' },
  { khoa: 'van-ban', nhan: t.chonLoai.vanBan, bieuTuong: 'but' },
  { khoa: 'cat-ghep', nhan: t.chonLoai.catGhep, bieuTuong: 'cat', toi: '/ban-tin/moi?loai=cat-ghep' },
  { khoa: 'truc-tiep', nhan: t.chonLoai.trucTiep, bieuTuong: 'song', toi: '/phat-song/truc-tiep' },
  { khoa: 'nguon', nhan: t.chonLoai.nguon, bieuTuong: 'tep', toi: '/ban-tin/kho-nguon', thieuApi: true },
];

export function ChonLoaiNoiDung(): ReactNode {
  return (
    <div className="man-hinh" data-man-hinh="chon-loai-noi-dung">
      <h1 className="chi-cho-doc-man-hinh">{vi.menuCon.khoNoiDungCaNhan}</h1>
      <Link to="/ban-tin" className="nut">
        <BieuTuongNoiDung ten="quay-lai" co={14} net={2} />
        {t.chonLoai.quayLai}
      </Link>
      <h2 className="nd-chon-loai__tieu-de">{t.chonLoai.tieuDe}</h2>
      <ul className="nd-luoi-o" role="list" data-vung="luoi-loai-noi-dung">
        {DANH_SACH_O.map((o) => {
          const noiDung = (
            <>
              <span className="nd-o__khoi">
                <BieuTuongNoiDung ten={o.bieuTuong} co={64} net={2} />
              </span>
              <span>{o.nhan}</span>
              {o.phu ? <small>{o.phu}</small> : null}
              {!o.toi || o.thieuApi ? <NhanChuaCoApi /> : null}
            </>
          );
          return (
            <li key={o.khoa}>
              {o.toi ? (
                <Link to={o.toi} className={`nd-o nd-o--${o.khoa}`} data-loai={o.khoa}>
                  {noiDung}
                </Link>
              ) : (
                <button
                  type="button"
                  className={`nd-o nd-o--${o.khoa}`}
                  data-loai={o.khoa}
                  aria-disabled="true"
                  title={t.chonLoai.khongKhaDung}
                >
                  {noiDung}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
